import { Autocomplete, TextField } from "@mui/material";
import { useFormik } from "formik";
import React, { useEffect, useState } from "react";
import {
  Button,
  Card,
  Col,
  Container,
  Form,
  Modal,
  Row,
  Spinner,
  Table,
} from "react-bootstrap";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import * as Yup from "yup";
import AddCustomerFormQuatation from "./Extra/AddCustomer";

import { Axios } from "api/axios";
import SubmitButton from "Components/Buttons/SubmitButton";
import { autocompleteDropdownStyles } from "Constants/Style";
import { urlToFile } from "helpers/functions/image_url_to_file";
import { useHandleBack } from "helpers/functions/useHandleBack";
import { useDispatch } from "react-redux";
import {
  addQuotation,
  fetchQuotationById,
  updateQuotation,
} from "slices/Quotation/thunk";
import Swal from "sweetalert2";
import BuildYourPC from "./BuildYourPC/BuildYourPC";
import AdditionalChargesSection, {
  AdditionalCharge,
} from "./Extra/AdditionalChargesSection";
import Standalone from "./Standalone/Standalone";
import Template from "./Template/Template";

// Removed unused scrollToTop function

interface Customer {
  customer_id: string;
  customer_name: string;
  mobile_no: string;
  email: string;
  // Optional fields that may come from API but are not sent in payload
  contact_person_name?: string;
  contact_person_mobile_no?: string;
  pan_number?: string;
  gstin?: string;
  address?: string;
  area?: string;
  city?: string;
  state?: string;
  country?: string;
  pincode?: string;
  status?: boolean;
  is_deleted?: boolean;
  created_by?: string;
  created_by_name?: string;
  created_at?: string;
  updated_by?: string;
  updated_by_name?: string;
  updated_at?: string;
}

interface QuotationType {
  quotation_date: string;
  validity: string;
  customer_details: Customer | null;
  quotation_creation_type: "build_your_pc" | "template" | "standalone";
  description: string;
  notes: string;
  overall_discount: string | number;
  overall_discount_type: "%" | "₹";
  subtotal: number;
  total_discount: number;
  net_taxable_value: number;
  total_gst_amount: number;
  quotation_total_amount: number;
}

export default function CreateQuotation() {
  // document.title = "Create Quotation";
  const handleBack = useHandleBack("/quotation");
  const [showCustomerModal, setShowCustomerModal] = useState(false);
  const [showAdditionalCharges, setShowAdditionalCharges] = useState(false);
  const [additionalChargesValidation, setAdditionalChargesValidation] =
    useState<{
      isValid: boolean;
      errors: string[];
    }>({ isValid: true, errors: [] });

  const [mandatoryCategoriesValidation, setMandatoryCategoriesValidation] =
    useState<{
      isValid: boolean;
      errors: string[];
    }>({ isValid: true, errors: [] });

  const today = new Date().toISOString().split("T")[0]; // YYYY-MM-DD format

  const { id } = useParams();
  const dispatch = useDispatch<any>();
  // const navigate = useNavigate();

  const [customersLoading, setCustomersLoading] = useState(false);

  const handleTabChange = (tabIndex: number) => {
    setTab(tabIndex);
    formik.setFieldValue(
      "quotation_creation_type",
      tabIndex === 1
        ? "build_your_pc"
        : tabIndex === 2
        ? "template"
        : "standalone"
    );
  };

  // Image validation helper functions
  const validateImageSize = (
    file: File
  ): { isValid: boolean; error?: string } => {
    const maxSizeInMB = 5; // Maximum file size in MB
    const maxSizeInBytes = maxSizeInMB * 1024 * 1024;

    if (file.size > maxSizeInBytes) {
      return {
        isValid: false,
        error: `Image size must be less than ${maxSizeInMB}MB. Current size: ${(
          file.size /
          (1024 * 1024)
        ).toFixed(2)}MB`,
      };
    }

    return { isValid: true };
  };

  const validateImageDimensions = (
    file: File
  ): Promise<{ isValid: boolean; error?: string }> => {
    return new Promise((resolve) => {
      const img = new Image();
      const url = URL.createObjectURL(file);

      img.onload = () => {
        URL.revokeObjectURL(url);
        // Target aspect ratio: 360:120 = 3:1
        const targetRatio = 360 / 120; // 3:1
        const currentRatio = img.width / img.height;
        const tolerance = 0.2; // ±0.2 tolerance for aspect ratio

        // Check if the aspect ratio is within tolerance
        const ratioDifference = Math.abs(currentRatio - targetRatio);

        if (ratioDifference > tolerance) {
          resolve({
            isValid: false,
            error: `Image aspect ratio must be approximately 360px : 120px (3:1). Uploaded image ratio: ${
              img.width
            }px : ${img.height}px (${currentRatio.toFixed(2)}:1)`,
          });
        } else {
          resolve({ isValid: true });
        }
      };

      img.onerror = () => {
        URL.revokeObjectURL(url);
        resolve({
          isValid: false,
          error: "Invalid image file",
        });
      };

      img.src = url;
    });
  };

  const validateImage = async (
    file: File
  ): Promise<{ isValid: boolean; error?: string }> => {
    // First check file size
    const sizeValidation = validateImageSize(file);
    if (!sizeValidation.isValid) {
      return sizeValidation;
    }

    // Then check dimensions
    const dimensionValidation = await validateImageDimensions(file);
    return dimensionValidation;
  };

  const prepareImages = async () => {
    // Process existing images with their positions preserved
    const existingFiles = await Promise.all(
      existingImages.map((img, i) => urlToFile(img.image, `image-${i}.jpg`))
    );

    const referenceExistingFiles = await Promise.all(
      existingReferenceImages.map((img, i) =>
        urlToFile(img.image, `reference-${i}.jpg`)
      )
    );

    // Rename uploaded images to maintain their position (0 or 1)
    const renamedUploadImages = uploadImages.map((file, i) => {
      const ext = file.name.split(".").pop();
      // Use the same index-based naming as existing images
      return new File([file], `image-${existingFiles.length + i}.${ext}`, {
        type: file.type,
      });
    });

    // Maintain order: first existing images, then new uploads
    const orderedFiles = [...existingFiles, ...renamedUploadImages].sort(
      (a, b) => {
        const getIndex = (name: string) => parseInt(name.split("-")[1]);
        return getIndex(a.name) - getIndex(b.name);
      }
    );

    return {
      allFiles: orderedFiles, // maintain order of main images
      allReferenceFiles: [...referenceExistingFiles, ...referenceImages], // reference images
    };
  };

  // Use local state for UI tab but sync with formik
  const [tab, setTab] = useState(1); // 1: Build Your PC, 2: Template, 3: Standalone

  // Separate item lists for each tab
  const [buildPCItemList, setBuildPCItemList] = useState<any[]>([]);
  const [templateItemList, setTemplateItemList] = useState<any[]>([]);
  const [standaloneItemList, setStandaloneItemList] = useState<any[]>([]);

  // Helper to get current tab's item list
  const getCurrentItemList = React.useCallback(() => {
    if (tab === 1) return buildPCItemList;
    if (tab === 2) return templateItemList;
    return standaloneItemList;
  }, [tab, buildPCItemList, templateItemList, standaloneItemList]);

  const [uploadImages, setUploadImages] = useState<File[]>([]);
  const [uploadImagesLinks, setUploadImagesLinks] = useState<string[]>([]);
  const [referenceImages, setReferenceImages] = useState<File[]>([]);
  const [existingImages, setExistingImages] = useState<
    { img_id: string; image: string }[]
  >([]);
  const [existingReferenceImages, setExistingReferenceImages] = useState<
    { img_id: string; image: string }[]
  >([]);

  // Track if user has attempted to submit to show validation errors
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);

  // Image validation state
  const [imageValidationErrors, setImageValidationErrors] = useState<{
    mainImages: string[];
    referenceImages: string[];
  }>({ mainImages: [], referenceImages: [] });

  // Helper function to get current total main images count
  const getCurrentMainImagesCount = () => {
    return uploadImages.length + existingImages.length;
  };

  // Additional Charges State
  const [assemblyCharges, setAssemblyCharges] = useState<AdditionalCharge>({
    price: "",
    discount: "",
    discount_type: "%",
    total: 0,
  });

  const [otherCharges, setOtherCharges] = useState<AdditionalCharge[]>([
    {
      price: "",
      discount: "",
      discount_type: "%",
      total: 0,
      remark: "",
      name: "",
    },
  ]);

  const [customers, setCustomers] = useState<any[]>([]);

  const [loading, setIsLoading] = useState(false);

  const [initialValues, setInitialData] = useState<QuotationType>({
    quotation_date: today,
    validity: "",
    customer_details: null,
    quotation_creation_type: "build_your_pc", // enum ["build_your_pc", "template", "standalone"]
    // assembly_charges: { description: "", amount: 0 },
    // other_charges: [] as Array<{ description: string; amount: number }>,
    description: "",
    notes: "",
    overall_discount: "",
    overall_discount_type: "%",
    subtotal: 0,
    total_discount: 0,
    net_taxable_value: 0,
    total_gst_amount: 0,
    quotation_total_amount: 0,
  });

  const formatDate = (isoString: string | null | undefined) => {
    if (!isoString) return "";
    return isoString.split("T")[0]; // returns "YYYY-MM-DD"
  };

  // Edit Quotation
  useEffect(() => {
    if (!id) return;
    setIsLoading(true);
    dispatch(fetchQuotationById(id))
      .unwrap()
      .then((data: any) => {
        // Check validity: only set if it has not expired
        let formattedValidity = "";
        if (data.validity) {
          const todayDate = new Date();
          const validityDate = new Date(formatDate(data.validity));
          // Remove the time part for accurate date-only comparison
          todayDate.setHours(0, 0, 0, 0);
          validityDate.setHours(0, 0, 0, 0);
          if (validityDate >= todayDate) {
            formattedValidity = formatDate(data.validity);
          } else {
            toast.error(
              "Validity date has expired. Please update the validity date."
            );
          }
        }

        setInitialData({
          ...data,
          quotation_date: formatDate(data.quotation_date),
          validity: formattedValidity,
          customer_details: data.customer_details || null,
          description: data.description || "",
        });

        // Set item list for correct tab only
        if (data.quotation_creation_type === "template") {
          setTemplateItemList(data.item_details || []);
        } else if (data.quotation_creation_type === "standalone") {
          setStandaloneItemList(data.item_details || []);
        } else {
          setBuildPCItemList(data.item_details || []);
        }

        setExistingImages(data.images || []);
        setUploadImagesLinks(
          (data.images || []).map((img: any) => img.url || "")
        );
        setExistingReferenceImages(data.reference_images || []);

        setAssemblyCharges(
          data.assembly_charges || {
            price: "",
            discount: "",
            discount_type: "%",
            total: 0,
          }
        );

        // NEW: Handle other_charges as an array
        setOtherCharges(
          Array.isArray(data.other_charges) && data.other_charges.length > 0
            ? data.other_charges
            : [
                {
                  price: "",
                  discount: "",
                  discount_type: "%",
                  total: 0,
                  remark: "",
                  name: "",
                },
              ]
        );

        // Set tab based on quotation_creation_type
        if (data.quotation_creation_type === "template") setTab(2);
        else if (data.quotation_creation_type === "standalone") setTab(3);
        else setTab(1);
      })
      .catch(() => toast.error("Failed to fetch Quotation"))
      .finally(() => setIsLoading(false));
  }, [id, dispatch]);

  // Fetch customers for the autocomplete
  useEffect(() => {
    const fetchCustomers = async () => {
      setCustomersLoading(true);
      try {
        const response = await Axios.get(
          `/api/customers/list-without-pagination/`
        );
        setCustomers(response.data.data);
      } catch (error) {
        console.error("Error fetching customers:", error);
        toast.error("Failed to load customers. Please refresh the page.");
      } finally {
        setCustomersLoading(false);
      }
    };
    fetchCustomers();
  }, []);

  const formik = useFormik({
    initialValues: initialValues,
    enableReinitialize: true,
    validate: (values) => {
      // console.log("values", values);
    },
    validationSchema: Yup.object({
      quotation_date: Yup.date().required("Quotation date is required"),
      validity: Yup.date().required("Validity date is required"),
      customer_details: Yup.object()
        .nullable()
        .required("Customer is required")
        .shape({
          customer_name: Yup.string().required(
            "Customer/Company name is required"
          ),
          email: Yup.string().email("Invalid email address"),
          mobile_no: Yup.string()
            .matches(/^\d+$/, "Mobile number must be digits only")
            .length(10, "Mobile number must be exactly 10 digits"),
        }),
      description: Yup.string(),
      notes: Yup.string(),
      overall_discount: Yup.string().test("discount-limit", function (value) {
        if (!value || value.trim() === "") return true;
        const numValue = parseFloat(value);
        if (isNaN(numValue)) return true; // Allow non-numeric strings

        const { overall_discount_type } = this.parent;
        if (overall_discount_type === "%") {
          if (numValue > 100) {
            return this.createError({
              message: "Percentage discount cannot exceed 100%",
            });
          }
        }
        // For flat amount, check against subtotal
        if (overall_discount_type === "₹") {
          const items = Object.values(getCurrentItemList()).flat();
          const itemsSubtotal = items.reduce(
            (acc, item) =>
              acc + (Number(item.selling_price) || 0) * (item.quantity || 1),
            0
          );
          if (numValue > itemsSubtotal) {
            return this.createError({
              message: `Discount amount cannot exceed subtotal of ₹${itemsSubtotal.toFixed(
                2
              )}`,
            });
          }
        }
        return true;
      }),
      quotation_creation_type: Yup.string().oneOf([
        "build_your_pc",
        "template",
        "standalone",
      ]),
    }),
    onSubmit: async (values) => {
      setHasAttemptedSubmit(true);
      const items = Object.values(getCurrentItemList()).flat();

      // Check if customer is selected
      const customer = values.customer_details;

      if (!customer || typeof customer !== "object") {
        toast.error("Please select a customer before submitting.");
        return;
      }

      // Check if mobile_no and email are present and are strings
      if (
        !customer.mobile_no ||
        typeof customer.mobile_no !== "string" ||
        customer.mobile_no.trim() === ""
      ) {
        toast.error("Please enter the customer's mobile number.");
        return;
      }

      if (
        !customer.email ||
        typeof customer.email !== "string" ||
        customer.email.trim() === ""
      ) {
        toast.error("Please enter the customer's email address.");
        return;
      }

      if (!items.length) {
        window.scrollTo({ top: 0, behavior: "smooth" });
        toast.error(
          "Please add at least one item to the quotation before submitting."
        );
        return;
      }

      // Check additional charges validation
      if (showAdditionalCharges && !additionalChargesValidation.isValid) {
        if (additionalChargesValidation.errors.length > 0) {
          toast.dismiss();
          toast.error(additionalChargesValidation.errors[0]);
        }
        return;
      }

      // Check mandatory categories validation (only for Build Your PC and Template)
      if (
        (values.quotation_creation_type === "build_your_pc" ||
          values.quotation_creation_type === "template") &&
        !mandatoryCategoriesValidation.isValid
      ) {
        if (mandatoryCategoriesValidation.errors.length > 0) {
          toast.dismiss();
          toast.error(mandatoryCategoriesValidation.errors[0]);
        }
        return;
      }

      // Check for main image validation errors only (images are optional)
      const hasImageValidationErrors =
        imageValidationErrors.mainImages.length > 0;

      if (hasImageValidationErrors) {
        window.scrollTo({ top: 0, behavior: "smooth" });
        toast.error("Please fix image validation errors before submitting.");
        return;
      }

      // Create form data
      const formData = new FormData();
      formData.append("quotation_date", values.quotation_date);
      formData.append("validity", values.validity);

      // Send only essential customer fields to reduce payload size
      // Only sending fields that are actually used in the application
      const essentialCustomerFields = {
        customer_id: customer.customer_id,
        customer_name: customer.customer_name,
        mobile_no: customer.mobile_no,
        email: customer.email,
        address: customer.address,
        city: customer.city,
        state: customer.state,
        country: customer.country,
        pincode: customer.pincode,
        gstin: customer.gstin,
        pan_number: customer.pan_number,
        contact_person_name: customer.contact_person_name,
        contact_person_mobile_no: customer.contact_person_mobile_no,
      };
      formData.append(
        "customer_details",
        JSON.stringify(essentialCustomerFields)
      );
      formData.append(
        "quotation_creation_type",
        values.quotation_creation_type
      );
      formData.append("assembly_charges", JSON.stringify(assemblyCharges));
      formData.append("other_charges", JSON.stringify(otherCharges));
      formData.append("description", values.description);
      formData.append("notes", values.notes);
      formData.append("subtotal", String(values.subtotal));
      formData.append("total_item_discount", String(values.total_discount));
      formData.append("net_taxable_value", String(values.net_taxable_value));
      formData.append("total_gst_amount", String(values.total_gst_amount));
      formData.append(
        "quotation_total_amount",
        String(values.quotation_total_amount)
      );

      formData.append("overall_discount", String(values.overall_discount));
      formData.append(
        "overall_discount_type",
        String(values.overall_discount_type)
      );

      formData.append("current_status", "Draft");

      // Append images
      // uploadImages.forEach((file) => {
      //   formData.append("images", file);
      // });

      // referenceImages.forEach((file) => {
      //   formData.append("reference_images", file);
      // });

      // Adds existing images from DB + newly uploaded images
      const { allFiles, allReferenceFiles } = await prepareImages();

      // console.log(allFiles);

      allFiles.forEach((file) => formData.append("images", file));
      allReferenceFiles.forEach((file) =>
        formData.append("reference_images", file)
      );

      //*// Image Links
      // 1
      // formData.append("image_urls", uploadImagesLinks[0]);
      // formData.append("image_urls", uploadImagesLinks[1]);

      // 2
      // uploadImagesLinks.forEach((link) => {
      //   formData.append("image_urls", link);
      // });
      uploadImagesLinks.forEach((link, index) => {
        formData.append(`image_urls[${index}]`, link);
      });

      //3
      // formData.append("image_urls", JSON.stringify(uploadImagesLinks));

      // console.log(itemList)

      formData.append("item_details", JSON.stringify(getCurrentItemList()));

      // Log form data
      // formData.forEach((value, key) => {
      //   console.log(key, value);
      // });

      try {
        // console.log(payload);
        if (id) {
          await dispatch(updateQuotation({ id, formData })).unwrap();
        } else {
          await dispatch(addQuotation(formData)).unwrap();
        }

        if (id) {
          toast.success("Quotation Updated Successfully!");
        } else {
          toast.success("Quotation Created Successfully!");
        }

        sessionStorage.setItem("QuotationContainerTab", "1");
        handleBack();
      } catch (error) {
        toast.error(
          id
            ? "Failed to update the quotation. Please try again."
            : "Failed to add the quotation. Please try again."
        );
      }

      // toast.success("Quotation saved successfully");
    },
  });

  useEffect(() => {
    if (formik.submitCount > 0 && Object.keys(formik.errors).length > 0) {
      const firstError = Object.entries(formik.errors)[0];

      if (firstError) {
        const [field, error] = firstError;
        if (error) {
          if (field === "customer_details") {
            const errorObj = firstError[1];
            // If errorObj is an object, get first key-value and show its value
            if (typeof errorObj === "object" && errorObj !== null) {
              const firstFieldError = Object.entries(errorObj)[0];
              if (firstFieldError && typeof firstFieldError[1] === "string") {
                toast.error(firstFieldError[1]);
                window.scrollTo({ top: 0, behavior: "smooth" }); // Always scroll for customer_details error
                return;
              }
            }
            return;
          }
          toast.error(`${error}`);
          if (field !== "overall_discount") {
            window.scrollTo({ top: 0, behavior: "smooth" });
          }
        }
      }
    }
  }, [formik.errors, formik.submitCount]);

  // Handle reference image uploads only
  const handleImageUpload = (
    e: React.ChangeEvent<HTMLInputElement>,
    type: "reference"
  ) => {
    if (!e.target.files) return;

    const files = Array.from(e.target.files);
    const maxLimit = 3; // for reference images
    const totalImages =
      existingReferenceImages.length + referenceImages.length + files.length;

    if (totalImages > maxLimit) {
      Swal.fire({
        text: `You can upload a maximum of ${maxLimit} reference images.`,
        icon: "warning",
        confirmButtonText: "Okay!",
        confirmButtonColor: "#d33",
        width: "250px",
        padding: "0.5rem",
        customClass: {
          popup: "swal2-sm-popup",
          confirmButton: "swal2-sm-btn",
        },
      });
      return;
    }

    setReferenceImages([...referenceImages, ...files]);
  };

  // Add inside component (below useFormik)
  const calculations = React.useMemo(() => {
    const items = Object.values(getCurrentItemList()).flat();

    // Assembly Charges
    const assemblyPrice = Number(assemblyCharges?.price || 0);
    const assemblyDiscount = Number(assemblyCharges?.discount || 0);
    const assemblyDiscountType = assemblyCharges?.discount_type || "%";

    // Items subtotal (only items, not including additional charges)
    const itemsSubtotal = items.reduce(
      (acc, item) =>
        acc + (Number(item.selling_price) || 0) * (item.quantity || 1),
      0
    );

    // Filter out empty other charges (no price or price is 0)
    const validOtherCharges = otherCharges.filter(
      (charge) => Number(charge.price || 0) > 0
    );

    // Calculate total other charges
    const totalOtherCharges = validOtherCharges.reduce(
      (sum, charge) => sum + Number(charge.price || 0),
      0
    );

    // Subtotal: only items (additional charges are shown separately)
    const subtotal = Math.round(itemsSubtotal);

    // Item-level discounts
    const totalItemDiscounts = Math.round(
      Math.max(
        items.reduce((acc, item) => {
          const discountAmount =
            item.discount_type === "%"
              ? ((Number(item.selling_price * item.quantity) || 0) *
                  (item.discount || 0)) /
                100
              : item.discount || 0;
          return acc + discountAmount;
        }, 0),
        0
      )
    );

    // Calculate assembly discount
    const assemblyDiscountValue = Math.round(
      Math.min(
        assemblyDiscountType === "%"
          ? (assemblyPrice * Number(assemblyDiscount || 0)) / 100
          : Number(assemblyDiscount || 0),
        assemblyPrice
      )
    );

    // Calculate total other discounts from valid other charges only
    const otherDiscountValue = validOtherCharges.reduce((sum, charge) => {
      const price = Number(charge.price || 0);
      const discount = Number(charge.discount || 0);
      let discountVal =
        charge.discount_type === "%" ? (price * discount) / 100 : discount;
      discountVal = Math.round(Math.min(discountVal, price));
      return sum + discountVal;
    }, 0);

    // Combine both
    const totalExtraDiscounts = Math.round(
      assemblyDiscountValue + otherDiscountValue
    );

    const totalDiscounts = Math.round(
      totalItemDiscounts + totalExtraDiscounts
    );

    // Calculate total amount before any discounts (subtotal + additional charges)
    const totalBeforeDiscounts = subtotal + assemblyPrice + totalOtherCharges;

    // Apply overall discount on the total amount before any discounts
    let overallDiscountValue = 0;

    if (formik.values.overall_discount_type === "%") {
      // For percentage: limit to 100% and calculate based on total before discounts
      const percentage = Math.min(
        Number(formik.values.overall_discount || 0),
        100
      );
      overallDiscountValue = Math.round(
        (totalBeforeDiscounts * percentage) / 100
      );
    } else {
      // For flat amount: limit to total before discounts
      const flatAmount = Number(formik.values.overall_discount || 0);
      overallDiscountValue = Math.round(
        Math.min(flatAmount, totalBeforeDiscounts)
      );
    }

    // Final total discount includes overall discount
    const finalTotalDiscounts = Math.round(
      totalDiscounts + overallDiscountValue
    );

    // Net taxable value = (items + additional charges) - total discounts
    const totalWithCharges = subtotal + assemblyPrice + totalOtherCharges;
    const netTaxableValue = Math.round(
      Math.max(totalWithCharges - finalTotalDiscounts, 0)
    );

    // GST calculation: 18% on Net Taxable Value
    const gstRate = 0.18;
    const totalGST = Math.round(netTaxableValue * gstRate);

    // Total Amount = Net Taxable Value + GST
    const totalAmount = Math.round(
      Math.max(netTaxableValue + totalGST, 0)
    );

    return {
      subtotal,
      totalDiscounts: finalTotalDiscounts,
      netTaxableValue,
      totalGST,
      overall_discount: overallDiscountValue,
      totalAmount,
    };
  }, [
    getCurrentItemList,
    assemblyCharges,
    otherCharges, // Now an array - UPDATED
    formik.values.overall_discount,
    formik.values.overall_discount_type,
  ]);

  useEffect(() => {
    formik.setFieldValue("subtotal", calculations.subtotal);
    formik.setFieldValue("total_discount", calculations.totalDiscounts);
    formik.setFieldValue("net_taxable_value", calculations.netTaxableValue);
    formik.setFieldValue("total_gst_amount", calculations.totalGST);
    formik.setFieldValue("quotation_total_amount", calculations.totalAmount);
    // eslint-disable-next-line
  }, [calculations]);

  // Clear main image validation errors when validation passes (images are optional)
  useEffect(() => {
    // Validation errors are cleared when images pass validation during upload
    // This useEffect can be kept for future use if needed
  }, [uploadImages.length, existingImages.length]);

  // console.log("uploadImages:",uploadImages, "existingImages:", existingImages, "uploadImagesLinks:", uploadImagesLinks);

  return (
    <>
      <div className="page-content bg-light min-vh-100">
        <Container fluid className="p-4 bg-white shadow-sm rounded mx-2">
          {/* Header */}
          <Row className="align-items-center g-2">
            <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-2 mb-3">
              {/* Left: Back icon and heading */}
              <div className="d-flex align-items-center gap-2">
                <span
                  style={{ fontSize: "24px", cursor: "pointer" }}
                  onClick={handleBack}
                >
                  ←
                </span>
                <h5 className="mb-0 fw-bold">
                  {id ? "Edit Quotation" : "Create Quotation"}
                </h5>
              </div>

              {/* Right: Breadcrumb */}
              <div className="text-muted">
                <span
                  onClick={handleBack}
                  style={{ color: "#6c757d", cursor: "pointer" }}
                >
                  Quotation
                </span>{" "}
                &gt;{" "}
                <span style={{ color: "#6c757d", cursor: "default" }}>
                  {id ? "Edit Quotation" : "Create Quotation"}
                </span>
              </div>
            </div>
          </Row>

          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
            </div>
          ) : (
            <form onSubmit={formik.handleSubmit} autoComplete="off">
              {/* Quotation & Customer Details */}
              <Row className="mb-3 g-3 ">
                {/* Quotation Details */}
                <Col xs={12} md={12} lg={6}>
                  <h5 className="mb-2 fw-semibold">
                    Quotation Details <span className="text-danger">*</span>
                  </h5>
                  <Card className="p-3">
                    <Row className="g-3 bgback rounded-3 p-2">
                      <Col xs={12} md={6}>
                        <Form.Group>
                          <Form.Label>
                            Quotation Date{" "}
                            <span className="text-danger">*</span>
                          </Form.Label>
                          <Form.Control
                            type="date"
                            name="quotation_date"
                            value={formik.values.quotation_date}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            isInvalid={
                              !!formik.errors.quotation_date &&
                              formik.touched.quotation_date
                            }
                          />
                          {formik.touched.quotation_date &&
                            formik.errors.quotation_date && (
                              <Form.Control.Feedback type="invalid">
                                {formik.errors.quotation_date}
                              </Form.Control.Feedback>
                            )}
                        </Form.Group>
                      </Col>
                      <Col xs={12} md={6}>
                        <Form.Group>
                          <Form.Label>
                            Validity <span className="text-danger">*</span>
                          </Form.Label>
                          <Form.Control
                            type="date"
                            name="validity"
                            min={today}
                            value={formik.values.validity}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            isInvalid={
                              !!formik.errors.validity &&
                              formik.touched.validity
                            }
                          />
                          {formik.touched.validity &&
                            formik.errors.validity && (
                              <Form.Control.Feedback type="invalid">
                                {formik.errors.validity}
                              </Form.Control.Feedback>
                            )}
                        </Form.Group>
                      </Col>
                    </Row>
                  </Card>

                  <Row>
                    {/* Upload Images Section */}
                    <Col md={6}>
                      <h6 className="mb-2">
                        Upload Images{" "}
                        <small className="text-muted">(Optional)</small>
                      </h6>
                      <div className="d-flex flex-column gap-2">
                        {/* First Image Upload */}
                        <div
                          className={`border rounded p-1 text-center position-relative rounded-3 ${
                            uploadImages.length > 0 ||
                            existingImages.length > 0 ||
                            (uploadImagesLinks[0] &&
                              uploadImagesLinks[0].trim() !== "")
                              ? "border-success"
                              : ""
                          }`}
                          style={{
                            minHeight: "20px",
                            cursor: "pointer",
                          }}
                        >
                          <input
                            type="file"
                            className="d-none"
                            accept="image/*"
                            id="uploadImage1"
                            onChange={async (e) => {
                              const file = e.target.files?.[0];
                              if (file) {
                                const validation = await validateImage(file);
                                if (validation.isValid) {
                                  // Calculate total before adding the new image
                                  const currentTotal =
                                    getCurrentMainImagesCount();

                                  if (currentTotal >= 2) {
                                    toast.error(
                                      "Maximum 2 main images allowed. Please remove an existing image first."
                                    );
                                    return;
                                  }

                                  setUploadImages((prev) => {
                                    const newImages = [...prev];
                                    newImages.push(file);
                                    return newImages;
                                  });

                                  // Clear any previous validation errors for this image
                                  setImageValidationErrors((prev) => ({
                                    ...prev,
                                    mainImages: prev.mainImages.filter(
                                      (_, index) => index !== 0
                                    ),
                                  }));

                                  // Clear validation errors will be handled by useEffect
                                } else {
                                  // Show validation error
                                  setImageValidationErrors((prev) => ({
                                    ...prev,
                                    mainImages: [
                                      ...prev.mainImages.filter(
                                        (_, index) => index !== 0
                                      ),
                                      validation.error || "Invalid image",
                                    ],
                                  }));
                                  toast.error(
                                    validation.error || "Invalid image"
                                  );
                                }
                              }
                            }}
                          />
                          <label
                            htmlFor="uploadImage1"
                            className="mb-0 cursor-pointer w-100 px-2"
                          >
                            <div className="text-muted d-flex justify-content-between align-items-center">
                              <span>Upload Image 1</span>
                              <i className="bi bi-cloud-upload fs-4"></i>
                            </div>
                          </label>
                        </div>

                        {/* Second Image Upload */}
                        <div
                          className={`border rounded p-1 text-center position-relative rounded-3 ${
                            uploadImages.length > 1 ||
                            existingImages.length > 1 ||
                            (uploadImagesLinks[1] &&
                              uploadImagesLinks[1].trim() !== "")
                              ? "border-success"
                              : ""
                          }`}
                          style={{
                            minHeight: "20px",
                            cursor: "pointer",
                          }}
                        >
                          <input
                            type="file"
                            className="d-none"
                            accept="image/*"
                            id="uploadImage2"
                            onChange={async (e) => {
                              const file = e.target.files?.[0];
                              if (file) {
                                const validation = await validateImage(file);
                                if (validation.isValid) {
                                  // Calculate total before adding the new image
                                  const currentTotal =
                                    getCurrentMainImagesCount();

                                  if (currentTotal >= 2) {
                                    toast.error(
                                      "Maximum 2 main images allowed. Please remove an existing image first."
                                    );
                                    return;
                                  }

                                  setUploadImages((prev) => {
                                    const newImages = [...prev];
                                    newImages.push(file);
                                    return newImages;
                                  });

                                  // Clear any previous validation errors for this image
                                  setImageValidationErrors((prev) => ({
                                    ...prev,
                                    mainImages: prev.mainImages.filter(
                                      (_, index) => index !== 1
                                    ),
                                  }));

                                  // Clear validation errors will be handled by useEffect
                                } else {
                                  // Show validation error
                                  setImageValidationErrors((prev) => ({
                                    ...prev,
                                    mainImages: [
                                      ...prev.mainImages.filter(
                                        (_, index) => index !== 1
                                      ),
                                      validation.error || "Invalid image",
                                    ],
                                  }));
                                  toast.error(
                                    validation.error || "Invalid image"
                                  );
                                }
                              }
                            }}
                          />
                          <label
                            htmlFor="uploadImage2"
                            className="mb-0 cursor-pointer w-100 px-2"
                          >
                            <div className="text-muted d-flex justify-content-between align-items-center">
                              <span>Upload Image 2</span>
                              <i className="bi bi-cloud-upload fs-4"></i>
                            </div>
                          </label>
                        </div>

                        {/* Image Count Indicator */}
                        <div className="mt-2">
                          <small className="fw-bold text-muted">
                            Images: {uploadImages.length + existingImages.length}
                            {uploadImages.length + existingImages.length > 0
                              ? " ✓"
                              : ""}
                          </small>
                        </div>

                        {/* Image Validation Errors */}
                        {imageValidationErrors.mainImages.length > 0 && (
                          <div className="mt-2">
                            {imageValidationErrors.mainImages.map(
                              (error, index) => (
                                <div key={index} className="text-danger small">
                                  <i className="bi bi-exclamation-triangle me-1"></i>
                                  {error}
                                </div>
                              )
                            )}
                          </div>
                        )}

                        {/* Display Area for Images */}
                        <div className="d-flex flex-wrap gap-2">
                          {/* Existing Images (from DB) */}
                          {existingImages?.map((img, index) => (
                            <div
                              key={img.img_id}
                              className="position-relative"
                              style={{ width: "120px", height: "40px" }}
                            >
                              <img
                                src={img.image}
                                alt={`existing-upload-${index + 1}`}
                                className="img-fluid rounded border"
                                style={{
                                  width: "100%",
                                  height: "100%",
                                  objectFit: "contain",
                                  padding: "2px",
                                }}
                              />
                              <button
                                type="button"
                                className="btn btn-sm btn-danger position-absolute top-0 end-0"
                                style={{ padding: "0 4px", fontSize: "12px" }}
                                onClick={() => {
                                  setExistingImages(
                                    existingImages.filter(
                                      (i) => i.img_id !== img.img_id
                                    )
                                  );
                                  // Clear any validation errors when removing existing images
                                  setImageValidationErrors((prev) => ({
                                    ...prev,
                                    mainImages: [],
                                  }));
                                }}
                              >
                                ×
                              </button>
                            </div>
                          ))}

                          {/* Newly Uploaded Images */}
                          {uploadImages?.map((file, index) => (
                            <div
                              key={index}
                              className="position-relative"
                              style={{ width: "120px", height: "40px" }}
                            >
                              <img
                                src={URL.createObjectURL(file)}
                                alt={`upload-${index + 1}`}
                                className="img-fluid rounded border"
                                style={{
                                  width: "100%",
                                  height: "100%",
                                  objectFit: "contain",
                                  padding: "2px",
                                }}
                              />
                              <button
                                type="button"
                                className="btn btn-sm btn-danger position-absolute top-0 end-0"
                                style={{ padding: "0 4px", fontSize: "12px" }}
                                onClick={() => {
                                  setUploadImages((prev: any) => {
                                    const newImages = [...prev];
                                    newImages.splice(index, 1);
                                    return newImages;
                                  });
                                  setUploadImagesLinks((prev) => {
                                    const newLinks = [...prev];
                                    newLinks.splice(index, 1);
                                    return newLinks;
                                  });
                                  // Clear all validation errors when removing images
                                  setImageValidationErrors((prev) => ({
                                    ...prev,
                                    mainImages: [],
                                  }));
                                }}
                              >
                                ×
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    </Col>

                    {/* Image Links */}
                    <Col md={6}>
                      <h6 className="mb-2">
                        Image Links{" "}
                        {/* <small className="text-muted">(Optional)</small> */}
                      </h6>
                      <div className="d-flex flex-column gap-2">
                        {/* First Link Input */}
                        <Form.Group controlId="imageLink1">
                          <Form.Control
                            type="text"
                            placeholder="Enter image URL 1 (optional)"
                            value={uploadImagesLinks[0] || ""}
                            disabled={
                              uploadImages.length === 0 &&
                              existingImages.length === 0
                            }
                            onChange={(e) =>
                              setUploadImagesLinks((prev) => {
                                const newLinks = [...prev];
                                newLinks[0] = e.target.value;
                                return newLinks;
                              })
                            }
                          />
                        </Form.Group>

                        {/* Second Link Input */}
                        <Form.Group controlId="imageLink2">
                          <Form.Control
                            type="text"
                            placeholder="Enter image URL 2 (optional)"
                            value={uploadImagesLinks[1] || ""}
                            disabled={
                              uploadImages.length < 2 &&
                              existingImages.length < 2
                            }
                            onChange={(e) =>
                              setUploadImagesLinks((prev) => {
                                const newLinks = [...prev];
                                newLinks[1] = e.target.value;
                                return newLinks;
                              })
                            }
                          />
                        </Form.Group>
                      </div>
                    </Col>
                  </Row>
                </Col>

                {/* Customer Details */}
                <Col xs={12} md={12} lg={6}>
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <h5 className="mb-0 fw-semibold">
                      Customer/Company Details
                    </h5>
                    <Button
                      size="sm"
                      // variant="primary"
                      className="btn btn-primary"
                      onClick={() => setShowCustomerModal(true)}
                    >
                      + Add Customer
                    </Button>
                  </div>

                  <Card className="p-3 bgback rounded">
                    <Row className="g-3  p-2">
                      <Col xs={12}>
                        <Form.Label>
                          Select Customer/Company Name{" "}
                          <span className="text-danger">*</span>
                        </Form.Label>
                        <Autocomplete
                          disablePortal
                          size="small"
                          fullWidth
                          options={customers}
                          getOptionLabel={(option) => {
                            return `${option.customer_name} (${option.mobile_no})`;
                          }}
                          value={formik.values.customer_details}
                          ListboxProps={autocompleteDropdownStyles.ListboxProps}
                          renderOption={(props, option) =>
                            autocompleteDropdownStyles.renderOption(
                              props,
                              option,
                              (option) =>
                                `${option.customer_name} (${option.mobile_no})`
                            )
                          }
                          onChange={(_, newValue) => {
                            try {
                              // Ensure we have a valid customer object
                              if (
                                newValue &&
                                typeof newValue === "object" &&
                                newValue !== null
                              ) {
                                // Validate that the customer object has the expected structure
                                const validCustomer = {
                                  customer_id: newValue.customer_id || "",
                                  customer_name: newValue.customer_name || "",
                                  mobile_no: newValue.mobile_no || "",
                                  email: newValue.email || "",
                                  contact_person_name:
                                    newValue.contact_person_name || "",
                                  contact_person_mobile_no:
                                    newValue.contact_person_mobile_no || "",
                                  pan_number: newValue.pan_number || "",
                                  gstin: newValue.gstin || "",
                                  address: newValue.address || "",
                                  area: newValue.area || "",
                                  city: newValue.city || "",
                                  state: newValue.state || "",
                                  country: newValue.country || "",
                                  pincode: newValue.pincode || "",
                                  status: newValue.status || true,
                                  is_deleted: newValue.is_deleted || false,
                                  created_by: newValue.created_by || "",
                                  created_by_name:
                                    newValue.created_by_name || "",
                                  created_by_firstlast:
                                    newValue.created_by_firstlast || "",
                                  updated_by: newValue.updated_by || "",
                                  updated_by_name:
                                    newValue.updated_by_name || "",
                                  updated_by_firstlast:
                                    newValue.updated_by_firstlast || "",
                                  created_at: newValue.created_at || "",
                                  updated_at: newValue.updated_at || "",
                                };
                                formik.setFieldValue(
                                  "customer_details",
                                  validCustomer
                                );
                              } else {
                                formik.setFieldValue("customer_details", null);
                              }
                            } catch (error) {
                              console.error("Error setting customer:", error);
                              formik.setFieldValue("customer_details", null);
                            }
                          }}
                          loading={customersLoading}
                          loadingText="Loading Customers..."
                          noOptionsText="No customers found"
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              placeholder="Select Customer/Company Name"
                              variant="outlined"
                              size="small"
                              sx={{
                                ...autocompleteDropdownStyles.inputSx,
                                backgroundColor: "#DDE1EF",
                                "& .MuiInputBase-root": {
                                  backgroundColor: "#ffffffff",
                                },
                                "& .MuiFormHelperText-root": {
                                  margin: 0,
                                  padding: "4px 6px",
                                  lineHeight: 1.4,
                                },
                              }}
                              error={
                                !!formik.errors.customer_details &&
                                formik.touched.customer_details
                              }
                              helperText={
                                formik.touched.customer_details &&
                                formik.errors.customer_details &&
                                typeof formik.errors.customer_details ===
                                  "string"
                                  ? formik.errors.customer_details
                                  : ""
                              }
                            />
                          )}
                        />
                      </Col>
                      <Col xs={12} md={6}>
                        <Form.Label>Contact Phone Number</Form.Label>
                        <Form.Control
                          placeholder="Mobile Number"
                          value={
                            typeof formik.values.customer_details?.mobile_no ===
                            "string"
                              ? formik.values.customer_details.mobile_no
                              : ""
                          }
                          onChange={(e) => {
                            const currentCustomer =
                              formik.values.customer_details || {};
                            formik.setFieldValue("customer_details", {
                              ...currentCustomer,
                              mobile_no: e.target.value,
                            });
                          }}
                        />
                      </Col>
                      <Col xs={12} md={6}>
                        <Form.Label>Email Address</Form.Label>
                        <Form.Control
                          placeholder="Email"
                          value={(() => {
                            try {
                              const customer = formik.values.customer_details;
                              if (!customer || typeof customer !== "object")
                                return "";

                              const email = customer.email;
                              // Ensure email is a string
                              if (typeof email === "string") {
                                return email;
                              }
                              return "";
                            } catch (error) {
                              console.error("Error accessing email:", error);
                              return "";
                            }
                          })()}
                          onChange={(e) => {
                            try {
                              const currentCustomer =
                                formik.values.customer_details || {};
                              // Ensure we don't create nested objects
                              const updatedCustomer = {
                                ...currentCustomer,
                                email: e.target.value,
                              };
                              // Validate that email is a string
                              if (typeof updatedCustomer.email === "string") {
                                formik.setFieldValue(
                                  "customer_details",
                                  updatedCustomer
                                );
                              }
                            } catch (error) {
                              console.error("Error updating email:", error);
                            }
                          }}
                        />
                      </Col>
                    </Row>
                  </Card>
                </Col>
              </Row>

              {/* Description Section */}
              <Row className="mb-3">
                <Col xs={12}>
                  <div className="border bgback pt-2 rounded-3 shadow-sm">
                    <div className="d-flex px-1 align-items-center mb-2 cursor-pointer">
                      <i className="ph ph-caret-down me-2 text-bold"></i>
                      <h5 className="mb-0 fw-semibold">Description</h5>
                    </div>
                    <Form.Control
                      as="textarea"
                      rows={1}
                      name="description"
                      placeholder="Enter Description"
                      className="shadow-sm mx-0 mb-0"
                      value={formik.values.description}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      style={{ maxHeight: "250px", overflowY: "auto" }}
                    />
                  </div>
                </Col>
              </Row>

              <hr />

              {/* Tabs */}
              <div className="my-3 d-flex gap-2" style={{ marginTop: "-10px" }}>
                {[1, 2, 3].map((buttonTab) => {
                  const isDisabled = !!id && tab !== buttonTab;
                  const label =
                    buttonTab === 1
                      ? "Build Your PC"
                      : buttonTab === 2
                      ? "Template"
                      : "Standalone";

                  return (
                    <div
                      key={buttonTab}
                      style={{ cursor: isDisabled ? "not-allowed" : "pointer" }}
                    >
                      <Button
                        onClick={() => handleTabChange(buttonTab)}
                        className={`btn ${
                          tab === buttonTab ? "btn-primary" : "btn-secondary"
                        }`}
                        disabled={isDisabled}
                      >
                        {label}
                      </Button>
                    </div>
                  );
                })}
              </div>

              {/* Tab Content */}
              {tab === 1 ? (
                <BuildYourPC
                  itemList={buildPCItemList}
                  setItemList={setBuildPCItemList}
                  isEdit={!!id}
                  onValidationChange={setMandatoryCategoriesValidation}
                />
              ) : tab === 2 ? (
                <Template
                  itemList={templateItemList}
                  setItemList={setTemplateItemList}
                  id={id}
                  onValidationChange={setMandatoryCategoriesValidation}
                />
              ) : (
                <Standalone
                  itemList={standaloneItemList}
                  setItemList={setStandaloneItemList}
                />
              )}

              {/* Additional Charges & Notes Section */}

              <div className="d-flex justify-content-end align-items-center mt-4">
                <Button
                  size="sm"
                  onClick={() => setShowAdditionalCharges((prev) => !prev)}
                  className={`p-2 btn ${
                    showAdditionalCharges ? "btn-primary" : "btn-secondary"
                  }`}
                >
                  {showAdditionalCharges ? "- " : "+ "} Additional Charges
                </Button>
              </div>

              {/* Additional Charges Component */}
              {showAdditionalCharges && (
                <AdditionalChargesSection
                  assemblyCharges={assemblyCharges}
                  otherCharges={otherCharges}
                  setAssemblyCharges={setAssemblyCharges}
                  setOtherCharges={setOtherCharges}
                  onValidationChange={(isValid, errors) => {
                    setAdditionalChargesValidation({ isValid, errors });
                  }}
                />
              )}

              {/* Notes & Reference Image Upload & Calculations Section */}
              <Row className="mt-4 smooth-transition">
                <Col xs={12} md={12} lg={6}>
                  {/* Notes */}
                  <div className="mb-4 border bgback pt-2  rounded-3 shadow-sm">
                    <div className="d-flex px-1 align-items-center mb-2 cursor-pointer">
                      <i className="ph ph-caret-down me-2 text-bold"></i>
                      <h5 className="mb-0 fw-semibold">Notes</h5>
                    </div>
                    <Form.Control
                      as="textarea"
                      rows={4}
                      name="notes"
                      placeholder="Enter Notes"
                      className="shadow-sm"
                      value={formik.values.notes}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      style={{ maxHeight: "250px", overflowY: "auto" }}
                    />
                  </div>

                  {/* Image Upload Section */}
                  <Row className="mb-4 g-3">
                    {/* Reference Images Section */}
                    <Col md={6}>
                      <h6 className="mb-2">Reference Images</h6>

                      <div
                        className="border rounded p-1 text-center position-relative rounded-3"
                        style={{ minHeight: "20px", cursor: "pointer" }}
                      >
                        <input
                          type="file"
                          className="d-none"
                          multiple
                          accept="image/*"
                          id="referenceImage"
                          onChange={(e) => handleImageUpload(e, "reference")}
                        />
                        <label
                          htmlFor="referenceImage"
                          className="mb-0 cursor-pointer w-100 px-2"
                        >
                          <div className="text-muted d-flex justify-content-between align-items-center">
                            <span>
                              Upload Image (
                              {existingReferenceImages.length +
                                referenceImages.length}
                              /3)
                            </span>
                            <i className="bi bi-cloud-upload fs-4"></i>
                          </div>
                        </label>
                      </div>

                      {/* Reference Image Validation Errors */}
                      {imageValidationErrors.referenceImages.length > 0 && (
                        <div className="mt-2">
                          {imageValidationErrors.referenceImages.map(
                            (error, index) => (
                              <div key={index} className="text-danger small">
                                <i className="bi bi-exclamation-triangle me-1"></i>
                                {error}
                              </div>
                            )
                          )}
                        </div>
                      )}

                      <div className="d-flex flex-wrap gap-2 mt-2">
                        {/* Existing Reference Images */}
                        {existingReferenceImages?.map((img, index) => (
                          <div
                            key={img.img_id || `existing-ref-${index}`}
                            className="position-relative"
                            style={{ width: "90px", height: "90px" }}
                          >
                            <img
                              src={img.image}
                              alt={`existing-reference-${index}`}
                              className="img-fluid rounded border"
                              style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "contain",
                                padding: "2px",
                              }}
                            />
                            <button
                              type="button"
                              className="btn btn-sm btn-danger position-absolute top-0 end-0"
                              style={{ padding: "0 4px", fontSize: "12px" }}
                              onClick={(e) => {
                                e.preventDefault();
                                const newImages = [...existingReferenceImages];
                                newImages.splice(index, 1);
                                setExistingReferenceImages(newImages);
                                // Clear validation errors when removing existing reference images
                                setImageValidationErrors((prev) => ({
                                  ...prev,
                                  referenceImages: [],
                                }));
                              }}
                            >
                              ×
                            </button>
                          </div>
                        ))}

                        {/* Newly Uploaded Reference Images */}
                        {referenceImages?.map((file, index) => (
                          <div
                            key={index}
                            className="position-relative"
                            style={{ width: "90px", height: "90px" }}
                          >
                            <img
                              src={URL.createObjectURL(file)}
                              alt={`reference-${index}`}
                              className="img-fluid rounded border"
                              style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "contain",
                                padding: "2px",
                              }}
                            />
                            <button
                              type="button"
                              className="btn btn-sm btn-danger position-absolute top-0 end-0"
                              style={{ padding: "0 4px", fontSize: "12px" }}
                              onClick={() => {
                                setReferenceImages((prev) => {
                                  const newImages = [...prev];
                                  newImages.splice(index, 1);
                                  return newImages;
                                });
                                // Clear all reference validation errors when removing images
                                setImageValidationErrors((prev) => ({
                                  ...prev,
                                  referenceImages: [],
                                }));
                              }}
                            >
                              ×
                            </button>
                          </div>
                        ))}
                      </div>
                    </Col>
                  </Row>
                </Col>

                {/* Calculations Section */}
                <Col xs={12} md={12} lg={6} className="mb-2">
                  <Card className="shadow-sm p-0 border bgback rounded">
                    <Card.Body className="p-2">
                      <div className="table-responsive">
                        <Table borderless className="mb-0">
                          <tbody>
                            {/* Extra Discount */}
                            <tr>
                              <td className="align-middle d-none d-sm-table-cell">
                                {/* Empty TD for spacing on larger screens */}
                              </td>
                              <td className="align-middle">
                                <div className="mb-1">
                                  <label className="form-label small mb-1">
                                    Extra Discount
                                  </label>
                                  <div className="row g-1">
                                    {/* Input */}
                                    <div className="col-12 col-md-8">
                                      <Form.Control
                                        type="text"
                                        name="overall_discount"
                                        placeholder="0"
                                        value={formik.values.overall_discount}
                                        onChange={(e) => {
                                          formik.handleChange(e);
                                          // Trigger validation immediately
                                          setTimeout(() => {
                                            formik.validateField(
                                              "overall_discount"
                                            );
                                          }, 0);
                                        }}
                                        onBlur={formik.handleBlur}
                                        className="w-100 form-control-sm"
                                        isInvalid={
                                          !!formik.errors.overall_discount &&
                                          formik.touched.overall_discount
                                        }
                                      />
                                      <Form.Control.Feedback type="invalid">
                                        {formik.errors.overall_discount}
                                      </Form.Control.Feedback>
                                    </div>
                                    {/* Select */}
                                    <div className="col-12 col-md-4">
                                      <Form.Select
                                        name="overall_discount_type"
                                        value={
                                          formik.values.overall_discount_type
                                        }
                                        onChange={(e) => {
                                          formik.handleChange(e);
                                          // Trigger validation of discount field when type changes
                                          setTimeout(() => {
                                            formik.validateField(
                                              "overall_discount"
                                            );
                                          }, 0);
                                        }}
                                        className="w-100 form-select-sm"
                                      >
                                        <option value="%">%</option>
                                        <option value="₹">₹</option>
                                      </Form.Select>
                                    </div>
                                  </div>
                                </div>
                              </td>
                            </tr>

                            {/* Calculations */}
                            <tr>
                              <td className="align-middle">
                                Subtotal (Products Total Before Discount)
                              </td>
                              <td className="text-end align-middle">
                                ₹ {calculations.subtotal.toFixed(2)}
                              </td>
                            </tr>
                            {assemblyCharges &&
                              Number(assemblyCharges.price || 0) > 0 && (
                                <tr>
                                  <td>Assembly Charges</td>
                                  <td className="text-end align-middle">
                                    ₹{" "}
                                    {Number(assemblyCharges.price || 0).toFixed(
                                      2
                                    )}
                                  </td>
                                </tr>
                              )}
                            {otherCharges &&
                              otherCharges.length > 0 &&
                              otherCharges
                                .filter(
                                  (charge) => Number(charge.price || 0) > 0
                                )
                                .map((charge, index) => (
                                  <tr key={index}>
                                    <td>{charge.name}</td>
                                    <td className="text-end align-middle">
                                      ₹ {Number(charge.price || 0).toFixed(2)}
                                    </td>
                                  </tr>
                                ))}
                            <tr>
                              <td className="align-middle">Total Discounts</td>
                              <td className="text-end align-middle">
                                ₹ {calculations.totalDiscounts.toFixed(2)}
                              </td>
                            </tr>
                            <tr className="fw-bold">
                              <td className="align-middle">
                                Net Taxable Value
                              </td>
                              <td className="text-end align-middle">
                                ₹ {calculations.netTaxableValue.toFixed(2)}
                              </td>
                            </tr>
                            <tr>
                              <td className="align-middle">Total GST (18%)</td>
                              <td className="text-end align-middle">
                                ₹ {calculations.totalGST.toFixed(2)}
                              </td>
                            </tr>
                            <tr className="fw-bold">
                              <td
                                className="align-middle"
                                style={{ fontSize: "1rem" }}
                              >
                                Total Amount
                              </td>
                              <td
                                className="text-end align-middle"
                                style={{ fontSize: "1rem" }}
                              >
                                ₹ {calculations.totalAmount.toFixed(2)}
                              </td>
                            </tr>
                          </tbody>
                        </Table>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>

              {/* Action Buttons */}
              <div className="d-flex justify-content-center gap-3 py-3">
                <Button
                  variant="outline-primary"
                  className="btn btn-secondary"
                  onClick={handleBack}
                  type="button"
                >
                  Cancel
                </Button>
                {/* <Button
                variant="primary"
                className="btn btn-primary"
                type="submit"
              >
                Save
              </Button> */}

                <SubmitButton
                  isSubmitting={formik.isSubmitting}
                  isEdit={!!id}
                  labels={{
                    create: "Save",
                    update: "Update",
                    submitting: "Saving...",
                    updating: "Updating...",
                  }}
                />
              </div>
            </form>
          )}

          {/* Add Customer Modal */}
          <Modal
            show={showCustomerModal}
            onHide={() => setShowCustomerModal(false)}
            size="xl"
            backdrop="static"
          >
            <Modal.Header closeButton>
              <Modal.Title>Add Customer</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <AddCustomerFormQuatation
                setShowCustomerModal={setShowCustomerModal}
                onCustomerAdded={(newCustomer) => {
                  setCustomers((prev) => [...prev, newCustomer]);
                  formik.setFieldValue("customer_details", newCustomer);
                }}
              />
            </Modal.Body>
          </Modal>
        </Container>
      </div>
    </>
  );
}
